import type { CalcularCotizacionInput, CalculoCotizacionDTO, CotizacionDTO, CotizacionInput, CotizacionResumenDTO, ImportacionCotizacionDTO, RetasaResultadoDTO, VersionCotizacionDTO } from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { get, nuevaClaveOperacion, post, put } from '@/lib/api.ts';

export interface FiltrosCotizaciones {
  q?: string | undefined;
  estado?: string | undefined;
  cliente_id?: string | undefined;
  pagina?: number | undefined;
  limite?: number | undefined;
}
export interface PaginaCotizaciones {
  datos: CotizacionResumenDTO[];
  total: number;
  pagina: number;
  limite: number;
}

export const clavesCotizaciones = {
  lista: (f: FiltrosCotizaciones) => ['cotizaciones', 'lista', f] as const,
  detalle: (id: string) => ['cotizaciones', 'detalle', id] as const,
  versiones: (id: string) => ['cotizaciones', 'versiones', id] as const,
};

export const useCotizaciones = (f: FiltrosCotizaciones) => useQuery({ queryKey: clavesCotizaciones.lista(f), queryFn: () => get<PaginaCotizaciones>('/cotizaciones', f as Record<string, string | number | undefined>), placeholderData: (p) => p });
export const useCotizacion = (id: string | null) => useQuery({ queryKey: clavesCotizaciones.detalle(id ?? ''), queryFn: () => get<CotizacionDTO>(`/cotizaciones/${id}`), enabled: !!id });
export const useVersionesCotizacion = (id: string | null, habilitado: boolean) => useQuery({ queryKey: clavesCotizaciones.versiones(id ?? ''), queryFn: () => get<VersionCotizacionDTO[]>(`/cotizaciones/${id}/versiones`), enabled: !!id && habilitado });
export const calcularCotizacion = (datos: CalcularCotizacionInput) => post<CalculoCotizacionDTO>('/cotizaciones/calcular', datos);
export const importarCotizacion = (id: string) => get<ImportacionCotizacionDTO>(`/cotizaciones/${id}/importar`);
export const registrarImpresionCotizacion = (id: string) => post<{ impresiones: number; copia: boolean }>(`/cotizaciones/${id}/impresion`);

function useInvalidar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['cotizaciones'] });
}
export function useCrearCotizacion() {
  const inv = useInvalidar();
  // Clave de operación del formulario: el doble clic o el reintento guardan una sola cotización (hallazgo T22). Se
  // renueva al guardar; si falla, el servidor no la reservó y sirve para el siguiente intento
  const clave = useRef(nuevaClaveOperacion());
  return useMutation({
    mutationFn: (datos: CotizacionInput) => post<CotizacionDTO>('/cotizaciones', datos, { headers: { 'idempotency-key': clave.current } }),
    onSuccess: () => {
      clave.current = nuevaClaveOperacion();
      void inv();
    },
  });
}
export function useEditarCotizacion(id: string) {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (datos: CotizacionInput) => put<CotizacionDTO>(`/cotizaciones/${id}`, datos), onSuccess: () => void inv() });
}
export function useRetasarCotizacion() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: (id: string) => post<RetasaResultadoDTO>(`/cotizaciones/${id}/retasar`), onSuccess: () => void inv() });
}
export function useAnularCotizacion() {
  const inv = useInvalidar();
  return useMutation({ mutationFn: ({ id, motivo }: { id: string; motivo: string }) => post<CotizacionDTO>(`/cotizaciones/${id}/anular`, { motivo }), onSuccess: () => void inv() });
}
